export const PERFECT_TIPS_BASE_STAKE = 100;
export const PERFECT_TIPS_OUTCOMES = ["1", "X", "2"];
export const MARKET_POWER_TOLERANCE = 1e-12;
export const MARKET_POWER_MAX_ITERATIONS = 100;

const probabilityOutcomes = (distribution) => {
  const keys = Object.keys(distribution || {});
  return keys.length >= 2 ? keys : PERFECT_TIPS_OUTCOMES;
};

const probabilityValues = (distribution, outcomes = probabilityOutcomes(distribution)) =>
  outcomes.map((outcome) => Number(distribution?.[outcome]));

export function isCompleteProbabilityDistribution(distribution) {
  const outcomes = probabilityOutcomes(distribution);
  const values = probabilityValues(distribution, outcomes);
  return (
    values.every((value) => Number.isFinite(value) && value > 0) &&
    Math.abs(values.reduce((sum, value) => sum + value, 0) - 1) <= 0.001
  );
}

export function normalizeImpliedProbabilities(distribution) {
  const outcomes = probabilityOutcomes(distribution);
  const values = probabilityValues(distribution, outcomes);
  if (!values.every((value) => Number.isFinite(value) && value > 0 && value < 1)) {
    return null;
  }

  const probabilitySum = (exponent) => values.reduce(
    (total, value) => total + value ** exponent,
    0
  );
  const rawSum = probabilitySum(1);
  if (!Number.isFinite(rawSum) || rawSum <= 0) return null;
  if (Math.abs(rawSum - 1) <= MARKET_POWER_TOLERANCE) {
    return Object.fromEntries(
      outcomes.map((outcome, index) => [outcome, values[index]])
    );
  }

  let lower = rawSum > 1 ? 1 : 0;
  let upper = rawSum > 1 ? 2 : 1;
  while (rawSum > 1 && probabilitySum(upper) > 1) {
    upper *= 2;
    if (!Number.isFinite(upper)) return null;
  }

  let exponent = 1;
  for (let iteration = 0; iteration < MARKET_POWER_MAX_ITERATIONS; iteration += 1) {
    exponent = (lower + upper) / 2;
    const sum = probabilitySum(exponent);
    if (Math.abs(sum - 1) <= MARKET_POWER_TOLERANCE) break;
    if (sum > 1) lower = exponent;
    else upper = exponent;
  }

  // Revised Odds / power de-vig: one shared exponent redistributes the
  // overround non-proportionally while preserving outcome order.
  const powered = values.map((value) => value ** exponent);
  return Object.fromEntries(
    outcomes.map((outcome, index) => [outcome, powered[index]])
  );
}

export function calculatePerfectTipsGame(
  bettyProbabilities,
  comparisonProbabilities,
  realizedOutcome = null,
  baseStake = PERFECT_TIPS_BASE_STAKE
) {
  if (
    !isCompleteProbabilityDistribution(bettyProbabilities) ||
    !isCompleteProbabilityDistribution(comparisonProbabilities) ||
    !Number.isFinite(Number(baseStake)) ||
    Number(baseStake) <= 0
  ) {
    return null;
  }

  const outcomesList = probabilityOutcomes(bettyProbabilities);
  if (
    outcomesList.some((outcome) => !Object.prototype.hasOwnProperty.call(comparisonProbabilities, outcome)) ||
    Object.keys(comparisonProbabilities || {}).length !== outcomesList.length
  ) return null;

  const outcomes = Object.fromEntries(
    outcomesList.map((outcome) => {
      const probabilityA = Number(bettyProbabilities[outcome]);
      const probabilityB = Number(comparisonProbabilities[outcome]);
      const stake = (probabilityA + probabilityB) * Number(baseStake);
      return [outcome, {
        probabilityA,
        probabilityB,
        stake,
        value: stake * (1 / probabilityB - 1 / probabilityA),
      }];
    })
  );
  const totalStake = outcomesList.reduce(
    (sum, outcome) => sum + outcomes[outcome].stake,
    0
  );

  return {
    outcomes,
    totalStake,
    realizedOutcome: outcomesList.includes(realizedOutcome)
      ? realizedOutcome
      : null,
    realizedValue: outcomesList.includes(realizedOutcome)
      ? outcomes[realizedOutcome].value
      : null,
  };
}

export function summarizePerfectTipsGames(results) {
  const realized = (Array.isArray(results) ? results : [])
    .map((result) => result?.realizedValue)
    .filter(Number.isFinite);
  const totalValue = realized.reduce((sum, value) => sum + value, 0);
  return {
    fixtures: realized.length,
    totalValue,
    valuePerFixture: realized.length ? totalValue / realized.length : null,
    positiveFixtures: realized.filter((value) => value > 0).length,
    negativeFixtures: realized.filter((value) => value < 0).length,
  };
}
